/**
 * The one canonical vocabulary for human authorization decisions in Compass.
 *
 * Capability keys are durable identifiers. Once policy documents persist a
 * key, renaming or removing it requires an explicit data migration. A
 * capability says what a person may do; it never grants a district, council,
 * account, or system scope by itself.
 */
export const CAPABILITY_SCOPES = ['self', 'subcommittee', 'district', 'council', 'account', 'system'];
export const CAPABILITY_AUTHORITIES = ['operational', 'account', 'system'];
export const CAPABILITY_SENSITIVITIES = ['standard', 'sensitive', 'highImpact'];
export const CAPABILITY_STATUSES = ['active', 'reserved'];
/**
 * Roles a person can hold in relation to ONE resource (a donor relationship, a unit) rather than through a Position.
 * A published policy decides which capabilities each role carries; a capability lists here only the roles it may ever be
 * granted to. Financial control, export, print, administration and assignment management are never role-grantable.
 */
export const RELATIONSHIP_ROLES = ['primary_coordinator', 'manager', 'unit_leader'];
function operational(definition) {
    return Object.freeze({
        ...definition,
        authority: 'operational',
        assignableToPositions: true,
        status: 'active',
    });
}
function tenantAdminOperational(definition) {
    return Object.freeze({
        ...definition,
        authority: 'operational',
        assignableToPositions: false,
        status: 'active',
    });
}
function account(definition) {
    return Object.freeze({
        ...definition,
        scopes: ['account'],
        authority: 'account',
        assignableToPositions: false,
        status: 'active',
    });
}
function system(definition) {
    return Object.freeze({
        ...definition,
        scopes: ['system'],
        authority: 'system',
        assignableToPositions: false,
        sensitivity: 'highImpact',
        status: 'active',
    });
}
export const CAPABILITY_DEFINITIONS = Object.freeze([
    account({
        key: 'accountAdministration.manageAdmins',
        domain: 'accountAdministration',
        name: 'Manage Compass Administrators',
        description: 'Bootstrap, grant, change, or remove direct Compass account-administrator access.',
        sensitivity: 'highImpact',
    }),
    account({
        key: 'accountAdministration.viewEntitlementDiagnostics',
        domain: 'accountAdministration',
        name: 'View Entitlement Diagnostics',
        description: 'Inspect resolved product-entitlement state for the current Compass account.',
        sensitivity: 'sensitive',
    }),
    account({
        key: 'accountAdministration.viewLoginActivity',
        domain: 'accountAdministration',
        name: 'View Login Activity',
        description: 'View account sign-in history and identity audit information.',
        sensitivity: 'sensitive',
    }),
    operational({
        key: 'actions.comment',
        domain: 'actions',
        name: 'Comment on Actions',
        description: 'Add an attributed note to an Action activity history.',
        scopes: ['self', 'subcommittee', 'district', 'council'],
        sensitivity: 'standard',
        requires: ['actions.view'],
    }),
    operational({
        key: 'actions.create',
        domain: 'actions',
        name: 'Create Actions',
        description: 'Create a manual Action in an authorized organizational scope.',
        scopes: ['subcommittee', 'district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'actions.edit',
        domain: 'actions',
        name: 'Edit Actions',
        description: 'Edit Action content, priority, and due-date fields.',
        scopes: ['self', 'subcommittee', 'district', 'council'],
        sensitivity: 'standard',
        requires: ['actions.view'],
    }),
    operational({
        key: 'actions.manageParticipants',
        domain: 'actions',
        name: 'Manage Action Participants',
        description: 'Add or remove people who participate in an Action.',
        scopes: ['self', 'subcommittee', 'district', 'council'],
        sensitivity: 'sensitive',
        requires: ['actions.view'],
    }),
    operational({
        key: 'actions.reassign',
        domain: 'actions',
        name: 'Reassign Actions',
        description: 'Change the accountable owner of an Action.',
        scopes: ['self', 'subcommittee', 'district', 'council'],
        sensitivity: 'sensitive',
        requires: ['actions.view'],
    }),
    operational({
        key: 'actions.updateStatus',
        domain: 'actions',
        name: 'Change Action Status',
        description: 'Advance, complete, cancel, or reopen an Action.',
        scopes: ['self', 'subcommittee', 'district', 'council'],
        sensitivity: 'standard',
        requires: ['actions.view'],
    }),
    operational({
        key: 'actions.view',
        domain: 'actions',
        name: 'View Actions',
        description: 'View Actions within the effective organizational or participation scope.',
        scopes: ['self', 'subcommittee', 'district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'advancement.previewAutomation',
        domain: 'advancement',
        name: 'Preview Advancement Automation',
        description: 'Preview generated advancement Actions without applying changes.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['advancement.view'],
    }),
    operational({
        key: 'advancement.runAutomation',
        domain: 'advancement',
        name: 'Run Advancement Automation',
        description: 'Manually invoke advancement reconciliation and its operational effects.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['advancement.view'],
    }),
    operational({
        key: 'advancement.view',
        domain: 'advancement',
        name: 'View Advancement Information',
        description: 'View imported advancement posture and generated operational information.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    account({
        key: 'applicationSettings.manage',
        domain: 'applicationSettings',
        name: 'Manage Application Settings',
        description: 'Change account-wide Compass configuration, organizational records, and shared vocabulary.',
        sensitivity: 'highImpact',
        requires: ['applicationSettings.view'],
    }),
    account({
        key: 'applicationSettings.view',
        domain: 'applicationSettings',
        name: 'View Application Settings',
        description: 'View account-wide Compass configuration and organizational settings.',
        sensitivity: 'sensitive',
    }),
    operational({
        key: 'communications.compose',
        domain: 'communications',
        name: 'Compose Communications',
        description: 'Prepare an outbound Compass communication and resolve its audience preview.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['communications.view'],
    }),
    operational({
        key: 'communications.send',
        domain: 'communications',
        name: 'Send Communications',
        description: 'Cause an external email communication to be delivered to an authorized audience.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['communications.compose'],
    }),
    operational({
        key: 'communications.view',
        domain: 'communications',
        name: 'View Communication History',
        description: 'View communication metadata and delivery history in an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'communications.viewContent',
        domain: 'communications',
        name: 'View Communication Content',
        description: 'View message bodies and recipient details for prior communications.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['communications.view'],
    }),
    operational({
        key: 'contacts.create',
        domain: 'contacts',
        name: 'Create Contacts',
        description: 'Add a person to an authorized district roster.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['contacts.view'],
    }),
    operational({
        key: 'contacts.delete',
        domain: 'contacts',
        name: 'Delete Contacts',
        description: 'Remove a Contact from an authorized district roster.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['contacts.view'],
    }),
    operational({
        key: 'contacts.edit',
        domain: 'contacts',
        name: 'Edit Contacts',
        description: 'Change roster and profile fields for a Contact.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['contacts.view'],
    }),
    operational({
        key: 'contacts.managePositions',
        domain: 'contacts',
        name: 'Manage Contact Positions',
        description: 'Assign or remove operational Positions on a Contact record.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['contacts.edit'],
    }),
    operational({
        key: 'contacts.view',
        domain: 'contacts',
        name: 'View Contacts',
        description: 'View Contact roster records without assuming access to protected contact details.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'contacts.viewContactInfo',
        domain: 'contacts',
        name: 'View Contact Information',
        description: 'View protected contact details such as email addresses and phone numbers.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['contacts.view'],
    }),
    tenantAdminOperational({
        key: 'dataImports.manage',
        domain: 'dataImports',
        name: 'Manage Data-Import Configuration',
        description: 'Configure import responsibility, reminders, ownership, and failure tracking.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['dataImports.viewStatus'],
    }),
    tenantAdminOperational({
        key: 'dataImports.perform',
        domain: 'dataImports',
        name: 'Perform Data Imports',
        description: 'Upload and apply supported operational source reports.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['dataImports.viewStatus'],
    }),
    tenantAdminOperational({
        key: 'dataImports.viewStatus',
        domain: 'dataImports',
        name: 'View Data-Import Status',
        description: 'View source-report freshness, status, and import outcomes.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    // Friends of Scouting. Authorization model: Position policy at district or council scope, plus published relationship-role
    // grants (an assignment chooses WHICH relationship; the council's policy chooses WHAT the role may do), plus the
    // administration plane. Identity, contact, notes, individual financial records, financial operations, financial control,
    // output, and aggregate progress are separate capabilities; none implies another. Campaign is a category, never a scope.
    operational({
        key: 'fos.administerCampaigns',
        domain: 'fos',
        name: 'Administer FOS Campaigns',
        description: 'Create and change fundraising campaigns, their campaign years and dates, and recognition levels; finalize recognition for a campaign year.',
        scopes: ['council'],
        sensitivity: 'sensitive',
        requires: ['fos.view'],
    }),
    operational({
        key: 'fos.administerGoals',
        domain: 'fos',
        name: 'Manage FOS Goals',
        description: 'Create and change official fundraising goals (council, district, campaign, unit) within the granted organizational scope. Never implied by a Position.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['fos.view'],
    }),
    operational({
        key: 'fos.administerSettings',
        domain: 'fos',
        name: 'Administer FOS Settings',
        description: 'Enable Friends of Scouting and change the council\'s fundraising configuration, financial-control settings (reconciliation, self-confirmation, write-off self-approval), time zone, and notification categories.',
        scopes: ['council'],
        sensitivity: 'highImpact',
    }),
    operational({
        key: 'fos.anonymizeDonors',
        domain: 'fos',
        name: 'Anonymize FOS Donors',
        description: 'Irreversibly remove personal identity from a donor record while preserving financial totals and audit references.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails'],
    }),
    operational({
        key: 'fos.approveAdjustments',
        domain: 'fos',
        name: 'Approve FOS Adjustments',
        description: 'Approve or reject requested pledge reductions and write-offs. Write-offs lower an outstanding balance and are never contributions received.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorHistory'],
    }),
    operational({
        key: 'fos.confirmPayments',
        domain: 'fos',
        name: 'Confirm FOS Payments',
        description: 'Confirm that a reported payment was received, verified against the council\'s records.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorHistory'],
    }),
    operational({
        key: 'fos.correctContributions',
        domain: 'fos',
        name: 'Correct FOS Contributions',
        description: 'Void and replace, move to another campaign year, or reverse a confirmed contribution through the audited correction workflow.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorHistory'],
    }),
    operational({
        key: 'fos.exportDonations',
        domain: 'fos',
        name: 'Export FOS Donations',
        description: 'Export the sensitive Friends of Scouting donation ledger. Separate from printing and from viewing.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails', 'fos.viewDonorHistory'],
    }),
    operational({
        key: 'fos.exportSummary',
        domain: 'fos',
        name: 'Export FOS Summaries',
        description: 'Export Friends of Scouting campaign summaries without donor-detail authority.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['fos.view'],
    }),
    operational({
        key: 'fos.manageAssignments',
        domain: 'fos',
        name: 'Manage FOS Donor Assignments',
        description: 'Assign, reassign, and end donor relationship managers; designate the primary coordinator; reassign in bulk.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails'],
    }),
    operational({
        key: 'fos.manageDonors',
        domain: 'fos',
        name: 'Manage FOS Donors',
        description: 'Create and maintain donor records, households, Person links and relationships, and clear contact restrictions.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails'],
    }),
    operational({
        key: 'fos.manageInteractions',
        domain: 'fos',
        name: 'Record FOS Interactions',
        description: 'Record donor interactions, relationship notes and follow-ups, and set contact preferences.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['fos.viewRelationshipNotes'],
        relationshipRoles: ['primary_coordinator', 'manager'],
    }),
    operational({
        key: 'fos.managePledges',
        domain: 'fos',
        name: 'Manage FOS Pledges',
        description: 'Record and amend pledges upward in an authorized scope, and request a pledge reduction or write-off.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorHistory'],
        relationshipRoles: ['primary_coordinator', 'manager'],
    }),
    operational({
        key: 'fos.mergeDonors',
        domain: 'fos',
        name: 'Merge FOS Donors',
        description: 'Review suggested duplicate donors and merge them on approval. Never automatic; financial history is preserved.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails'],
    }),
    operational({
        key: 'fos.printDonorSummary',
        domain: 'fos',
        name: 'Print FOS Donor Summary',
        description: 'Print the standard current-year donor summary. Printing never grants permission to view restricted information.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails', 'fos.viewDonorHistory'],
    }),
    operational({
        key: 'fos.produceRecognitionLists',
        domain: 'fos',
        name: 'Produce FOS Recognition Lists',
        description: 'Print or export recognition-only lists (name, campaign year, level) without amounts; anonymity preferences are honored.',
        scopes: ['council'],
        sensitivity: 'sensitive',
        requires: ['fos.viewDonorDetails'],
    }),
    operational({
        key: 'fos.reconcilePayments',
        domain: 'fos',
        name: 'Reconcile FOS Payments',
        description: 'Match confirmed payments to a deposit or accounting record. Meaningful only where the council has enabled reconciliation.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorHistory'],
    }),
    operational({
        key: 'fos.reportPayments',
        domain: 'fos',
        name: 'Report FOS Payments Received',
        description: 'Report a payment as received. A reported payment awaits council confirmation and never counts as received.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorHistory'],
        relationshipRoles: ['primary_coordinator', 'manager'],
    }),
    operational({
        key: 'fos.reviewAudit',
        domain: 'fos',
        name: 'Review FOS Audit',
        description: 'Review the Friends of Scouting audit trail, including sensitive-access records.',
        scopes: ['council'],
        sensitivity: 'highImpact',
    }),
    operational({
        key: 'fos.runImports',
        domain: 'fos',
        name: 'Run FOS Imports',
        description: 'Stage, review, and commit Friends of Scouting data imports.',
        scopes: ['council'],
        sensitivity: 'highImpact',
    }),
    operational({
        key: 'fos.sendSolicitations',
        domain: 'fos',
        name: 'Send FOS Solicitations',
        description: 'Send fundraising solicitations (the Friends of Scouting appeal message type) through Communications to an audience in this scope. Needs Send Communications for the same audience; managing donor activity never carries it, and no relationship role can hold it.',
        scopes: ['district', 'council'],
        requires: ['communications.send'],
        sensitivity: 'sensitive',
    }),
    operational({
        key: 'fos.view',
        domain: 'fos',
        name: 'View FOS Summaries',
        description: 'View Friends of Scouting goals, progress, and percentages. Aggregate only: no donor identities or individual contributions.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        relationshipRoles: ['unit_leader'],
    }),
    operational({
        key: 'fos.viewDonorContactInfo',
        domain: 'fos',
        name: 'View FOS Donor Contact Information',
        description: 'View a donor\'s email, phone, address, and contact preferences within an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails'],
        relationshipRoles: ['primary_coordinator', 'manager'],
    }),
    operational({
        key: 'fos.viewDonorDetails',
        domain: 'fos',
        name: 'View FOS Donor Details',
        description: 'View donor identities and relationship summaries. Does not include contact details, notes, or any financial record.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.view'],
        relationshipRoles: ['primary_coordinator', 'manager'],
    }),
    operational({
        key: 'fos.viewDonorHistory',
        domain: 'fos',
        name: 'View Historical FOS Donor Activity',
        description: 'View individual financial records — pledges, payments, balances by campaign and year — within an authorized scope. Requires the daily financial-access acknowledgment.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails'],
        relationshipRoles: ['primary_coordinator', 'manager'],
    }),
    operational({
        key: 'fos.viewRelationshipNotes',
        domain: 'fos',
        name: 'View FOS Relationship Notes',
        description: 'View private interaction history and relationship notes for donor relationships in an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['fos.viewDonorDetails'],
        relationshipRoles: ['primary_coordinator', 'manager'],
    }),
    operational({
        key: 'goals.archive',
        domain: 'goals',
        name: 'Archive Goals',
        description: 'Archive or restore a strategic Goal.',
        scopes: ['council'],
        sensitivity: 'sensitive',
        requires: ['goals.view'],
    }),
    operational({
        key: 'goals.create',
        domain: 'goals',
        name: 'Create Goals',
        description: 'Create a council strategic Goal.',
        scopes: ['council'],
        sensitivity: 'sensitive',
        requires: ['goals.view'],
    }),
    operational({
        key: 'goals.delete',
        domain: 'goals',
        name: 'Delete Goals',
        description: 'Permanently remove a strategic Goal.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['goals.view'],
    }),
    operational({
        key: 'goals.edit',
        domain: 'goals',
        name: 'Edit Goals',
        description: 'Change council strategic Goal content and organizational placement.',
        scopes: ['council'],
        sensitivity: 'sensitive',
        requires: ['goals.view'],
    }),
    operational({
        key: 'goals.view',
        domain: 'goals',
        name: 'View Goals',
        description: 'View council strategic Goals.',
        scopes: ['council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'initiatives.create',
        domain: 'initiatives',
        name: 'Create Initiatives',
        description: 'Create or adopt an Initiative in an authorized district and committee scope.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
        requires: ['initiatives.view'],
    }),
    operational({
        key: 'initiatives.delete',
        domain: 'initiatives',
        name: 'Delete Initiatives',
        description: 'Permanently remove an Initiative in an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['initiatives.view'],
    }),
    operational({
        key: 'initiatives.edit',
        domain: 'initiatives',
        name: 'Edit Initiatives',
        description: 'Edit, archive, or restore an Initiative in an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
        requires: ['initiatives.view'],
    }),
    operational({
        key: 'initiatives.view',
        domain: 'initiatives',
        name: 'View Initiatives',
        description: 'View Initiatives in an authorized district or council scope.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    account({
        key: 'permissions.manage',
        domain: 'permissions',
        name: 'Manage Permission Policy',
        description: 'Configure account authorization policy and future Position-to-capability assignments.',
        sensitivity: 'highImpact',
        requires: ['permissions.view'],
    }),
    account({
        key: 'permissions.view',
        domain: 'permissions',
        name: 'View Permission Policy',
        description: 'Inspect account authorization policy and effective configuration.',
        sensitivity: 'sensitive',
    }),
    operational({
        key: 'renewal.previewAutomation',
        domain: 'renewal',
        name: 'Preview Renewal Automation',
        description: 'Preview generated recharter and renewal Actions without applying changes.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['renewal.view'],
    }),
    operational({
        key: 'renewal.runAutomation',
        domain: 'renewal',
        name: 'Run Renewal Automation',
        description: 'Manually invoke recharter reconciliation and its operational effects.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['renewal.view'],
    }),
    operational({
        key: 'renewal.view',
        domain: 'renewal',
        name: 'View Renewal Information',
        description: 'View recharter, renewal, and generated operational information.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'strategy.manage',
        domain: 'strategy',
        name: 'Manage Strategic Plan',
        description: 'Create, edit, archive, restore, or remove strategic priorities.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['strategy.view'],
    }),
    operational({
        key: 'strategy.view',
        domain: 'strategy',
        name: 'View Strategic Plan',
        description: 'View council strategic priorities and plan structure.',
        scopes: ['council'],
        sensitivity: 'standard',
    }),
    system({
        key: 'systemAdministration.manageBackups',
        domain: 'systemAdministration',
        name: 'Manage Customer Backups',
        description: 'List, create, and download managed customer backups from Compass Manage.',
    }),
    system({
        key: 'systemAdministration.manageCustomers',
        domain: 'systemAdministration',
        name: 'Manage Customer Accounts',
        description: 'Administer Compass customer records at the product-system level.',
    }),
    system({
        key: 'systemAdministration.manageEntitlements',
        domain: 'systemAdministration',
        name: 'Manage Product Entitlements',
        description: 'Inspect or change commercial product/module entitlements in the separate product-admin plane.',
    }),
    operational({
        key: 'tasks.assign',
        domain: 'tasks',
        name: 'Assign Tasks',
        description: 'Change the Position or Contact assignment for a Task.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['tasks.view'],
    }),
    operational({
        key: 'tasks.comment',
        domain: 'tasks',
        name: 'Comment on Tasks',
        description: 'Add an attributed note to a Task.',
        scopes: ['self', 'district', 'council'],
        sensitivity: 'standard',
        requires: ['tasks.view'],
    }),
    operational({
        key: 'tasks.create',
        domain: 'tasks',
        name: 'Create Tasks',
        description: 'Create a Task under a Goal, Initiative, or standalone operational context.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
        requires: ['tasks.view'],
    }),
    operational({
        key: 'tasks.delete',
        domain: 'tasks',
        name: 'Delete Tasks',
        description: 'Permanently remove a Task.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['tasks.view'],
    }),
    operational({
        key: 'tasks.edit',
        domain: 'tasks',
        name: 'Edit Tasks',
        description: 'Edit Task content, schedule, and workflow fields other than assignment.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
        requires: ['tasks.view'],
    }),
    operational({
        key: 'tasks.updateStatus',
        domain: 'tasks',
        name: 'Change Task Status',
        description: 'Change status or progress notes on an assigned or managed Task.',
        scopes: ['self', 'district', 'council'],
        sensitivity: 'standard',
        requires: ['tasks.view'],
    }),
    operational({
        key: 'tasks.view',
        domain: 'tasks',
        name: 'View Tasks',
        description: 'View Tasks in an authorized district or council scope.',
        scopes: ['self', 'district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'training.previewAutomation',
        domain: 'training',
        name: 'Preview Training Automation',
        description: 'Preview generated training and hazardous-weather Actions without applying changes.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['training.view'],
    }),
    operational({
        key: 'training.runAutomation',
        domain: 'training',
        name: 'Run Training Automation',
        description: 'Manually invoke training reconciliation and its operational or communication effects.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['training.view'],
    }),
    operational({
        key: 'training.view',
        domain: 'training',
        name: 'View Training Information',
        description: 'View leader training, compliance, and generated operational information.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
    }),
    operational({
        key: 'unitMap.view',
        domain: 'unitMap',
        name: 'View Unit Map',
        description: 'View the sanitized Unit Map projection in an authorized district or council scope.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'units.export',
        domain: 'units',
        name: 'Export Unit Information',
        description: 'Export authorized Unit Health, leader, training, advancement, and renewal information.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['units.view', 'contacts.viewContactInfo'],
    }),
    operational({
        key: 'units.manageCommissionerAssignments',
        domain: 'units',
        name: 'Manage Unit Commissioner Assignments',
        description: 'Assign or remove Unit Commissioners for Units in an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
        requires: ['units.view'],
    }),
    operational({
        key: 'units.recomputeHealth',
        domain: 'units',
        name: 'Recompute Unit Health Summaries',
        description: 'Manually invoke tenant Unit Health summary reconciliation.',
        scopes: ['council'],
        sensitivity: 'highImpact',
        requires: ['units.view'],
    }),
    operational({
        key: 'units.view',
        domain: 'units',
        name: 'View Units',
        description: 'View Unit Health and operational Unit information in an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'units.viewCommissionerCoverage',
        domain: 'units',
        name: 'View Commissioner Coverage',
        description: 'View Unit Commissioner assignment and vacancy status without receiving operational authority over unassigned Units.',
        scopes: ['district', 'council'],
        sensitivity: 'standard',
    }),
    operational({
        key: 'userAccess.manageOperational',
        domain: 'userAccess',
        name: 'Manage Operational Access',
        description: 'Grant or remove non-administrator Compass access derived from operational Position assignments.',
        scopes: ['district', 'council'],
        sensitivity: 'highImpact',
        requires: ['userAccess.view'],
    }),
    operational({
        key: 'userAccess.view',
        domain: 'userAccess',
        name: 'View Operational Access',
        description: 'View operational memberships and pending access grants in an authorized scope.',
        scopes: ['district', 'council'],
        sensitivity: 'sensitive',
    }),
]);
export const CAPABILITY_KEYS = Object.freeze(CAPABILITY_DEFINITIONS.map((definition) => definition.key));
export const CAPABILITY_DOMAINS = Object.freeze([...new Set(CAPABILITY_DEFINITIONS.map((definition) => definition.domain))]);
export const CAPABILITY_REGISTRY = Object.freeze(Object.fromEntries(CAPABILITY_DEFINITIONS.map((definition) => [definition.key, definition])));
export function isCapabilityKey(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(CAPABILITY_REGISTRY, value);
}
export function assertCapabilityKey(value) {
    if (!isCapabilityKey(value))
        throw new TypeError(`Unknown Compass capability: ${String(value)}`);
}
export function getCapabilityDefinition(key) {
    return CAPABILITY_REGISTRY[key];
}
export function listCapabilities() {
    return CAPABILITY_DEFINITIONS;
}
export function listAssignableCapabilities() {
    return CAPABILITY_DEFINITIONS.filter((definition) => definition.assignableToPositions);
}
/** Active operational capabilities a published policy may grant to a relationship role. */
export function listRelationshipRoleCapabilities(role) {
    return CAPABILITY_DEFINITIONS.filter((definition) => definition.status === 'active' && definition.authority === 'operational' && definition.assignableToPositions
        && (definition.relationshipRoles ?? []).includes(role));
}
export function listCapabilitiesByDomain(domain) {
    return CAPABILITY_DEFINITIONS.filter((definition) => definition.domain === domain);
}
export * from './coverage.js';
